import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, ObservableCollection, PropertyChangedJoinPoint } from '@skylab-shared/core';
import { ClientKind, DocumentClientModel, DocumentPaymentListItemModel, ExchangeRateModel, SaleDocumentKind, VatTableItemModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, last, sum } from '@skylab-shared/utils';
import { ContextKeys } from '../../common/context-keys';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../../currency/queries/currency.query-base';
import { AmountOfPaymentGreaterThanDocumentValueError } from '../../documents/violations/amount-of-payment-greater-than-document-value-error';
import { OnePaymentOnCashBasisDocumentError } from '../../documents/violations/one-payment-on-cash-basis-document-error';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';
import { isAdvance, isAdvanceAdjustment } from '../utils/sale-utils';
import { NetValueHigherThan15000Warning } from '../violations/net-value-too-high-for-cash-payment-method-warning';

interface DocumentModelPart extends ModelBase {
    advanceValue?: number;
    advanceReturnValue?: number;
    cashBasis: boolean;
    currencyId: number;
    exchangeRate: ExchangeRateModel;
    grossValue: number;
    kind: SaleDocumentKind;
    paymentMethodId: number;
    payments: ObservableCollection<DocumentPaymentListItemModel>;
    vatTableItemsInBaseCurrency: ObservableCollection<VatTableItemModel>;
    dueDate: Date;
    dueDateInDays: number;
    client: DocumentClientModel;
}

const B2B_CASH_PAYMENT_LIMIT = 15000;

export class SaleDocumentPaymentsBoPart extends BusinessObjectPart<DocumentModelPart> {
    public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);

        this.registerAmountOfPaymentsValidationRule();
        this.registerOnePaymentOnCashBasisDocumentValidationRule();
        this.registerNetValueTooHighForCashPaymentValidationRules();
        this.setDueDateAccordingToPaymentMethod();
    }

    private setDueDateAccordingToPaymentMethod(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['paymentMethodId'])(
            () => {
                const paymentMethod = this.bo.localContext.firstPreloaded(PaymentMethodQuery, { id: this.bo.model.paymentMethodId })!;
                this.bo.model.dueDateInDays = paymentMethod.dueDate;
            }
        )));
    }

    private registerAmountOfPaymentsValidationRule(): void {
        const getPaymentsSum = (payments: ObservableCollection<DocumentPaymentListItemModel>) => {
            const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId })!;
            return Calc.round(payments.reduce((sum, payment) => sum + payment.value, 0), precision, MidpointRounding.AwayFromZero);
        };

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(DocumentPaymentListItemModel, ['value'])(
            () => this.engine.validation.queueValidation([DocumentPaymentListItemModel, 'value'])
        )));

        this.engine.validation.addRule(
            [[this.modelType, 'grossValue']],
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                const lastPayment = last(ctx.bo.model.payments);
                if (([SaleDocumentKind.AdvanceInvoice, SaleDocumentKind.AdvanceInvoiceAdjustment].indexOf(ctx.bo.model.kind) !== -1) || ctx.target !== lastPayment) {
                    return true;
                }
                const paymentsSum = getPaymentsSum(ctx.bo.model.payments);
                const invalidForPositiveValue = this.bo.model.grossValue >= 0 && paymentsSum > this.bo.model.grossValue;
                const invalidForNegativeValue = this.bo.model.grossValue < 0 && paymentsSum > Math.abs(this.bo.model.grossValue);
                return !(invalidForPositiveValue || invalidForNegativeValue);
            },
            AmountOfPaymentGreaterThanDocumentValueError
        );
    }

    private registerOnePaymentOnCashBasisDocumentValidationRule(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['grossValue', 'advanceValue', 'advanceReturnValue'])(
            () => this.engine.validation.queueValidation([DocumentPaymentListItemModel, 'value'])
        )));
        this.engine.validation.addRule(
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                if (!ctx.bo.model.cashBasis)
                    return true;
                const atMostOnePayment = ctx.bo.model.payments.length <= 1;
                const paymentItemValue = ctx.source.value;
                if (isAdvance(ctx.bo.model))
                    return atMostOnePayment && paymentItemValue === ctx.bo.model.advanceValue;
                if (isAdvanceAdjustment(ctx.bo.model))
                    return atMostOnePayment && paymentItemValue === ctx.bo.model.advanceReturnValue;
                return atMostOnePayment && Math.abs(paymentItemValue) === Math.abs(ctx.bo.model.grossValue);
            },
            OnePaymentOnCashBasisDocumentError
        );
    }

    private registerNetValueTooHighForCashPaymentValidationRules(): void {
        const isReceipt = () => SaleDocumentKind.Receipt === this.bo.model.kind;

        this.engine.validation.addRule(
            [[this.modelType, 'client'], [this.modelType, 'dueDate'], [this.modelType, 'paymentMethodId'], [this.modelType, 'grossValue'], [this.modelType, 'currencyId'], [this.modelType, 'kind'], [this.modelType, 'advanceValue'], [this.modelType, 'advanceReturnValue']],
            [[this.modelType, 'paymentMethodId']],
            _ => {
                if (!this.bo.model.client && isReceipt()) return true;
                if (this.bo.model.client?.kind === ClientKind.Person) return true;

                return this.isNetValueTooHighForCashPayment();
            },
            NetValueHigherThan15000Warning
        );
    }

    private isNetValueTooHighForCashPayment(): boolean {
        if (!this.bo.model.paymentMethodId) {
            return true;
        }

        if (this.bo.model.kind === SaleDocumentKind.Invoice && this.bo.model.payments) {
            const isInvoiceIssueToReceipt = this.bo.localContext.getPreloaded(ContextKeys.isInvoiceToReceipt(this.bo.model.sun));
            if (isInvoiceIssueToReceipt) return true;
        }

        const paymentMethod = this.bo.localContext.firstPreloaded(PaymentMethodQuery, { id: this.bo.model.paymentMethodId })!;
        if (!paymentMethod.cashPayment)
            return true;

        if (this.bo.model.kind === SaleDocumentKind.AdvanceInvoice) {
            return !((this.bo.model.advanceValue ?? 0) > B2B_CASH_PAYMENT_LIMIT);
        } else if (this.bo.model.kind === SaleDocumentKind.AdvanceInvoiceAdjustment) {
            return !((this.bo.model.advanceReturnValue ?? 0) > B2B_CASH_PAYMENT_LIMIT);
        } else if (!this.bo.model.exchangeRate) {
            return !(this.bo.model.grossValue > B2B_CASH_PAYMENT_LIMIT);
        } else {
            const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery)!;
            const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })!;
            const value = Calc.round(sum(this.bo.model.vatTableItemsInBaseCurrency.asArray(), i => i.grossValue), precision, MidpointRounding.AwayFromZero);
            return !(value > B2B_CASH_PAYMENT_LIMIT);
        }
    }
}
